// Tiny in-app screen state — DESIGN.md deliberately asks for no router library (5-ish screens).
// We still keep the browser back button harmless: every navigate() pushes a hash history entry,
// and popstate just re-derives the in-app screen from location.hash. Reloading or navigating
// back therefore always lands on a valid, renderable screen — never a blank page or a JS error.
import { reactive } from 'vue'

export type Screen =
  | 'home'
  | 'newMatch'
  | 'live'
  | 'pastMatches'
  | 'pastMatchDetail'
  | 'players'
  | 'versus'
  | 'player'
  | 'tournaments'
  | 'tournamentDetail'
  | 'voiceTraining'
  | 'debug'
  | 'debugTts'

export interface NavParams {
  matchId?: number
  tournamentId?: number
  playerId?: number
  playerAId?: number
  playerBId?: number
}

interface NavStateEntry {
  screen: Screen
  params: NavParams
}

function parseHash(hash: string): NavStateEntry {
  const raw = hash.replace(/^#/, '')
  const [screenPart, queryPart] = raw.split('?')
  const params: NavParams = {}
  if (queryPart) {
    const qs = new URLSearchParams(queryPart)
    const matchId = qs.get('matchId')
    const playerId = qs.get('playerId')
    const playerAId = qs.get('a')
    const playerBId = qs.get('b')
    const tournamentId = qs.get('tournamentId')
    if (matchId) params.matchId = Number(matchId)
    if (tournamentId) params.tournamentId = Number(tournamentId)
    if (playerId) params.playerId = Number(playerId)
    if (playerAId) params.playerAId = Number(playerAId)
    if (playerBId) params.playerBId = Number(playerBId)
  }
  // Round-4 feedback item 1: /rankings merged into /players (the Players page IS the ranking
  // now — rank #, avatar, name, elo, W-L, plus a search field) — redirect the old screen name so
  // a stale bookmark/back-button hash never lands on a vanished page.
  const resolvedScreenPart = screenPart === 'rankings' ? 'players' : screenPart
  const knownScreens: Screen[] = [
    'home',
    'newMatch',
    'live',
    'pastMatches',
    'pastMatchDetail',
    'players',
    'versus',
    'player',
    'tournaments',
    'tournamentDetail',
    'voiceTraining',
    'debug',
    'debugTts',
  ]
  const screen = knownScreens.includes(resolvedScreenPart as Screen) ? (resolvedScreenPart as Screen) : 'home'
  return { screen, params }
}

function encodeHash(screen: Screen, params: NavParams): string {
  const qs = new URLSearchParams()
  if (params.matchId != null) qs.set('matchId', String(params.matchId))
  if (params.tournamentId != null) qs.set('tournamentId', String(params.tournamentId))
  if (params.playerId != null) qs.set('playerId', String(params.playerId))
  if (params.playerAId != null) qs.set('a', String(params.playerAId))
  if (params.playerBId != null) qs.set('b', String(params.playerBId))
  const q = qs.toString()
  return `#${screen}${q ? `?${q}` : ''}`
}

export const nav = reactive<NavStateEntry>(parseHash(location.hash))

export function navigate(screen: Screen, params: NavParams = {}) {
  nav.screen = screen
  nav.params = params
  const hash = encodeHash(screen, params)
  if (location.hash !== hash) history.pushState({ screen, params }, '', hash)
}

/** Replace the current entry instead of pushing (used for the initial "reload lands on Live" jump). */
export function replaceScreen(screen: Screen, params: NavParams = {}) {
  nav.screen = screen
  nav.params = params
  const hash = encodeHash(screen, params)
  history.replaceState({ screen, params }, '', hash)
}

window.addEventListener('popstate', () => {
  const entry = parseHash(location.hash)
  nav.screen = entry.screen
  nav.params = entry.params
})
